import { useEffect } from "react";
import { styled } from "@glitz/react";
import Base, { ContentWidth } from "../components/basic/Base";
import { spacing, black, darkGrey, fontSize, fontWeight, green } from "../theme";

const DOC_TITLE = "Köpvillkor, retur & integritet | Trirebels";

const TermsPage = () => {
  useEffect(() => {
    const prev = document.title;
    document.title = DOC_TITLE;
    return () => {
      document.title = prev;
    };
  }, []);

  return (
    <PageOuter>
      <Base contentWidth={ContentWidth.Narrow}>
        <Article>
          <Title>Köpvillkor, retur &amp; integritet</Title>
          <Intro>
            Här beskriver vi betalning, priser, leverans, ångerrätt, reklamation,
            personuppgifter och kontakt för Trirebels (RE Design).
          </Intro>

          <Section>
            <H2>Betalning</H2>
            <Ul>
              <Li>
                <strong>Swish:</strong> enkelt och snabbt med Swish.
              </Li>
              <Li>
                <strong>Kort:</strong> betala med kredit- eller bankkort via Stripe.
              </Li>
              <Li>
                <strong>Klarna:</strong> betala med Klarna via Stripe (när det erbjuds i kassan).
              </Li>
            </Ul>
          </Section>

          <Section>
            <H2>Pris</H2>
            <P>
              Alla köp sker i svenska kronor (SEK). I priserna ingår moms enligt gällande
              skatteregler.
            </P>
          </Section>

          <Section>
            <H2>Leveransvillkor</H2>
            <P>
              Varor skickas med PostNord. Fri frakt vid order över 999 kr (gäller ej cyklar).
              Normalt skickas order inom 1–3 vardagar om varan finns i lager, med reservation
              för förseningar hos transportören.
            </P>
            <P>
              Finns varan inte i lager och leverans beräknas ta längre än en vecka kontaktar vi
              dig med preliminärt leveransdatum (gäller främst cyklar).
            </P>
          </Section>

          <Section>
            <H2>Frakt</H2>
            <P>
              Frakt beror på valt leveranssätt och varuklass. Exempel på ordinarie priser i
              kassan: standard 99 kr, standard lätt 59 kr, express 149 kr, express lätt 109 kr;
              hämta i butik 0 kr. Vid order över 999 kr är frakten fri enligt villkoren ovan
              (gäller ej cyklar).
            </P>
            <P>
              <strong>Cykel:</strong> frakt 699 kr om inget annat anges i kassan.
            </P>
          </Section>

          <Section>
            <H2>Garanti</H2>
            <P>
              Garanti lämnas enligt konsumentköplagen. Kontakta oss gärna på{" "}
              <ExtLink href="mailto:hello@trirebels.se">hello@trirebels.se</ExtLink> om något inte känns rätt,
              så löser vi det i första hand tillsammans med dig.
            </P>
            <P>
              Mer om dina rättigheter:{" "}
              <ExtLink
                href="https://www.konsumentverket.se/konsumentfragor/dina-rattigheter/kop-i-butik-och-vid-dorraffarer/konsumentkopslagen/"
                target="_blank"
                rel="noopener noreferrer"
              >
                Konsumentverket om konsumentköplagen
              </ExtLink>
              .
            </P>
          </Section>

          <Section>
            <H2>Ångerrätt</H2>
            <P>
              Enligt distansavtalslagen har du som konsument rätt att ångra köpet inom 14 dagar
              från att du mottagit varan, utan att ange skäl, under förutsättning att produkten och
              förpackningen är i väsentligen oförändrat skick — oanvänd och inte förstörd eller
              skadad. Du får undersöka varan försiktigt på samma sätt som i en fysisk butik.
            </P>
            <P>
              Trirebels tillämpar distansavtalslagen. Vid ånger står du som kund för returfrakten.
              Kontakta oss via{" "}
              <ExtLink href="mailto:hello@trirebels.se">hello@trirebels.se</ExtLink> för returnummer (RN)
              innan du skickar tillbaka varan.
            </P>
            <P>
              Vid transportskada ska Trirebels kontaktas inom skälig tid för att vi ska kunna
              hantera ärendet.
            </P>
            <P>
              Läs mer:{" "}
              <ExtLink
                href="https://www.konsumentverket.se/konsumentfragor/dina-rattigheter/reklamation-och-garanti/distansavtal-och-dorraffarer/"
                target="_blank"
                rel="noopener noreferrer"
              >
                Konsumentverket om distansavtal
              </ExtLink>
              .
            </P>
          </Section>

          <Section>
            <H2>Reklamation</H2>
            <P>
              Vid reklamation ska ett verkligt fel kunna visas. Behåll den defekta produkten och
              använd den inte i väntan på besked. Kontrollera gärna varan direkt vid mottagande.
            </P>
            <P>
              Kontakta{" "}
              <ExtLink href="mailto:hello@trirebels.se">hello@trirebels.se</ExtLink> med ordernummer och
              beskrivning av felet. Vid retur på grund av reklamation behöver du RN (returnummer)
              och returinstruktioner från oss; RN är giltigt under angiven tid.
            </P>
            <P>
              Förpacka alltid returer så att varan inte skadas under transport. Enligt
              konsumentköplagen har du som konsument rätt till rimlig hjälp vid berättigad
              reklamation, inklusive hur returfrakt hanteras enligt lag och vår rutin för ärendet.
            </P>
            <P>
              Om retur sker som reklamation men fel inte kan styrkas kan en avgift tillkomma för
              undersökning, frakt och hantering enligt separat information i ärendet. Sändningar
              utan avtalat RN kan returneras till avsändaren mot kostnad.
            </P>
            <P>
              Trirebels står för returfrakt när godset är felaktigt eller skadat i enlighet med
              gällande rätt.
            </P>
            <P>
              <ExtLink
                href="https://www.konsumentverket.se/konsumentfragor/dina-rattigheter/kop-i-butik-och-vid-dorraffarer/konsumentkopslagen/"
                target="_blank"
                rel="noopener noreferrer"
              >
                Konsumentverket om konsumentköplagen
              </ExtLink>
            </P>
          </Section>

          <Section>
            <H2>Återbetalning</H2>
            <P>
              Återbetalning sker normalt inom 14 dagar från att vi mottagit och godkänt returen
              eller reklamationen, i enlighet med gällande lag.
            </P>
            <P>
              <ExtLink
                href="https://www.konsumentverket.se/konsumentfragor/dina-rattigheter/reklamation-och-garanti/distansavtal-och-dorraffarer/"
                target="_blank"
                rel="noopener noreferrer"
              >
                Konsumentverket om distansavtal och ånger
              </ExtLink>
            </P>
          </Section>

          <Section>
            <H2>Returer</H2>
            <P>
              Vi följer lagen om distansavtal. För retur utanför ångerrätt/reklamation gäller samma
              kontaktväg: maila{" "}
              <ExtLink href="mailto:hello@trirebels.se">hello@trirebels.se</ExtLink> för RN och instruktioner.
              Returfrakt betalas av konsumenten om inget annat följer av lag eller överenskommelse.
            </P>
          </Section>

          <Section>
            <H2>Integritet &amp; personuppgifter</H2>
            <P>
              När du beställer hos oss behandlar vi personuppgifter som namn, adress, e-post och
              telefonnummer för att fullgöra köpet, kommunicera om ordern och — om du samtycker
              eller enligt berättigat intresse där det är tillåtet — skicka relevant information
              och erbjudanden. Du kan när som helst invända mot direktmarknadsföring eller begära
              registerutdrag, rättelse eller radering enligt dataskyddsförordningen (GDPR).
            </P>
            <P>
              Vi delar inte dina uppgifter med tredje part utöver vad som krävs för betalning,
              leverans och lagstadgade krav (t.ex. betalnings- och fraktpartners).
            </P>
            <P>
              Kontakt för dataskydd:{" "}
              <ExtLink href="mailto:hello@trirebels.se">hello@trirebels.se</ExtLink>.
            </P>
            <P>
              Om personnummer begärs i kassan används det endast i det syfte som anges vid
              tillfället (t.ex. ålderskontroll enligt vår rutin).
            </P>
          </Section>

          <Section>
            <H2>Tvist</H2>
            <P>
              Vår målsättning är att du ska vara nöjd. Vid tvist följer Trirebels allmänna
              reklamationsnämndens rekommendationer där så är tillämpligt.
            </P>
          </Section>

          <Section>
            <H2>Varors beskaffenhet</H2>
            <P>
              Vi strävar efter att information på webbplatsen (bild, beskrivning, pris) ska vara
              korrekt. Små avvikelser i färg eller utförande kan förekomma beroende på skärm och
              produktion.
            </P>
          </Section>

          <Section>
            <H2>Kontakt &amp; företagsuppgifter</H2>
            <P>
              <strong>E-post:</strong>{" "}
              <ExtLink href="mailto:hello@trirebels.se">hello@trirebels.se</ExtLink>
            </P>
            <P>
              <strong>RE Design</strong>
              <br />
              Brogatan 39
              <br />
              703 58 Örebro
              <br />
              Org.nr 860716-6611
            </P>
          </Section>
        </Article>
      </Base>
    </PageOuter>
  );
};

const PageOuter = styled.div({
  padding: { top: spacing.xl, bottom: spacing.xxxl, x: spacing.m },
  color: black,
  minHeight: "60vh",
});

const Article = styled.article({
  maxWidth: "720px",
  margin: { x: "auto" },
});

const Title = styled.h1({
  fontSize: fontSize.xl,
  fontWeight: fontWeight.heavy,
  margin: { bottom: spacing.m },
  lineHeight: 1.2,
});

const Intro = styled.p({
  fontSize: fontSize.m,
  color: darkGrey,
  margin: { bottom: spacing.xl },
  lineHeight: 1.5,
});

const Section = styled.section({
  margin: { bottom: spacing.xl },
});

const H2 = styled.h2({
  fontSize: fontSize.l,
  fontWeight: fontWeight.bold,
  margin: { bottom: spacing.s },
  color: green,
});

const P = styled.p({
  fontSize: fontSize.m,
  lineHeight: 1.6,
  margin: { bottom: spacing.m },
  color: black,
});

const Ul = styled.ul({
  margin: { bottom: spacing.m },
  paddingLeft: spacing.l,
});

const Li = styled.li({
  fontSize: fontSize.m,
  lineHeight: 1.6,
  margin: { bottom: spacing.xs },
});

const ExtLink = styled.a({
  color: green,
  textDecoration: "underline",
  ":hover": {
    color: black,
  },
});

export default TermsPage;
